import { Radio, RadioGroup, Stack, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';

import { FeatureFlag } from '@revelio/core';

import { useTabContentConfig } from '../../../next/CustomDash/components/PageConfigProvider/PageConfigProvider';
import { useSupportsStateGeography } from '../../../next/CustomDash/filters/hooks/useSupportsStateGeography';
import { useUnleashFlag } from '../../unleash-flags/use-unleash-flag';
import { PreferenceEditorSectionHeading } from '../components/PreferenceEditorSectionHeading';
import {
  type GeographyMode,
  PreferenceScope,
  UserPreferenceKey,
} from '../types';
import { PreferenceBrowserStorage } from '../utils/preference-session-storage';
import type {
  PreferenceEditorProps,
  PreferenceMeta,
} from './preferenceEditors';

export const meta: PreferenceMeta = {
  id: UserPreferenceKey.Geography,
  label: 'Geography',
};

export default function GeographyEditor({
  onPreferenceUpdate,
  preferences,
  diff,
}: PreferenceEditorProps) {
  const geographyUserPreferenceEnabled = useUnleashFlag(
    FeatureFlag.GeographyUserPreference
  );
  const { revelioPageId } = useTabContentConfig();
  const supportsStateGeography = useSupportsStateGeography(revelioPageId);
  const [mode, setMode] = useState<GeographyMode>('msa');

  useEffect(() => {
    const currentMode = preferences[UserPreferenceKey.Geography] || 'msa';
    setMode(currentMode);
  }, [preferences]);

  const onUpdate = useCallback(
    (value: GeographyMode) => {
      onPreferenceUpdate?.(UserPreferenceKey.Geography, value);
      setMode(value);
      PreferenceBrowserStorage.update(
        PreferenceScope.GLOBAL,
        UserPreferenceKey.Geography,
        value
      );
    },
    [onPreferenceUpdate]
  );

  if (!geographyUserPreferenceEnabled && !supportsStateGeography) {
    return null;
  }

  return (
    <>
      <PreferenceEditorSectionHeading
        title="Geography"
        indicateDiff={diff?.includes(meta.id) ?? false}
        tooltip="The geography boundaries used for filtering and map visualization."
      />
      <RadioGroup
        value={mode}
        onChange={(val) => onUpdate(val as GeographyMode)}
        colorScheme="green"
        size="sm"
        mt={3}
      >
        <Stack gap={3}>
          <Stack gap={1}>
            <Radio value="msa">
              Statistical boundaries (Metropolitan Areas)
            </Radio>
            <Text ml={5} fontSize="xs" color="gray.500" lineHeight="1.4">
              Region {'>'} Country {'>'} Metropolitan Area {'>'} City
            </Text>
          </Stack>
          <Stack gap={1}>
            <Radio value="administrative" isDisabled={!supportsStateGeography}>
              Administrative boundaries (States/Counties)
            </Radio>
            <Text ml={5} fontSize="xs" color="gray.500" lineHeight="1.4">
              Region {'>'} Country {'>'} State {'>'} City
            </Text>
            {!supportsStateGeography && (
              <Text ml={5} fontSize="xs" color="orange.500" lineHeight="1.4">
                State geography is not available on this page
              </Text>
            )}
          </Stack>
        </Stack>
      </RadioGroup>
    </>
  );
}
